/**
 * RangeFilterTags (KINDW-103 / FSD PLP-07)
 *
 * Renders a row of clickable filter chips above the Range Detail PLP grid. Multi-select toggle:
 * clicking a chip adds the (attributeId, value) to the active refine list; clicking again removes
 * it. Counts come from the latest `productSearch.refinements` response so they update as the user
 * filters.
 *
 * The component is intentionally presentational — it doesn't own refine state and doesn't fetch.
 * `<LazyLoadGrid>` lifts state and wires it to `useLazyProductSearch`.
 *
 * Renders `null` when:
 *   - `whitelist` is empty (Contentful field not configured for this range), or
 *   - there are no SCAPI refinement values and no active user selections to show.
 *
 * KINDW-1572: when a filter combo yields zero hits, SFCC often omits refinements from the response.
 * Chips that remain in `activeRefine` are still rendered (selected) so the user can deselect them.
 */
import React from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import PropTypes from 'prop-types';

import {
  Box,
  Button,
  Flex,
  useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';

import {
  isWhitelistedRefinementAttribute,
  parseRefineEntries,
} from '@amblique/king-living/app/components/range-filter-tags/refine-utils';

// Refinements we never want exposed as click chips on the Range Detail PLP — keep aligned with the
// legacy ProductList REFINEMENT_DISALLOW_LIST so a "do not show" decision applies to both UIs.
export const FORCED_DISALLOW = new Set(['c_isNew', 'cgid', 'htype', 'price']);

// SCAPI returns refinement values as strings regardless of the underlying attribute type. We treat
// any group whose values are all in this set as "boolean" and collapse it into a single on/off
// toggle chip labelled with the *attribute* display name (rather than `true`/`false`). Covers the
// usual SFCC patterns: real booleans (`true`/`false`), tinyint flags (`1`/`0`), and yes/no enums.
const BOOLEAN_VALUE_RE = /^(true|false|1|0|yes|no)$/i;
const BOOLEAN_TRUE_RE = /^(true|1|yes)$/i;
const isBooleanGroup = (group) =>
  Array.isArray(group?.values) &&
  group.values.length > 0 &&
  group.values.every((v) => BOOLEAN_VALUE_RE.test(String(v?.value)));

/**
 * Find a refinement object in `productSearch.refinements[]` by attributeId.
 */
const findRefinement = (refinements, attributeId) =>
  refinements?.find((r) => r?.attributeId === attributeId);

/**
 * Test if `(attributeId, value)` is currently in the active refine list.
 *
 * SCAPI refine entries look like `c_seaterCount=3|4` for multi-select on the same attribute. We
 * split on `=`/`|` rather than substring-checking to avoid false positives when one value is a
 * prefix of another (e.g. `1` vs `10`).
 */
export const isRefineValueActive = (activeRefine, attributeId, value) => {
  for (const entry of activeRefine ?? []) {
    if (typeof entry !== 'string') continue;
    const eqIdx = entry.indexOf('=');
    if (eqIdx <= 0) continue;
    const key = entry.slice(0, eqIdx);
    if (key !== attributeId) continue;
    const values = entry.slice(eqIdx + 1).split('|');
    if (values.includes(String(value))) return true;
  }
  return false;
};

const formatFallbackLabel = (value) => {
  const text = String(value);
  if (!text) return text;
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const resolveValueLabel = (refinementLabels, attributeId, apiValue) =>
  refinementLabels?.values?.get(`${attributeId}:${apiValue}`) ??
  formatFallbackLabel(apiValue);

const resolveAttributeLabel = (refinementLabels, attributeId) =>
  refinementLabels?.attributes?.get(attributeId) ?? attributeId;

/**
 * Build the chip list from SCAPI refinements plus any active selections SFCC omitted (KINDW-1572).
 *
 * @param {object} params
 * @param {object[]} [params.refinements]
 * @param {string[]} [params.whitelist]
 * @param {string[]} [params.activeRefine]
 * @param {{ values?: Map<string, string>, attributes?: Map<string, string> }} [params.refinementLabels]
 * @returns {object[]}
 */
export const buildRangeFilterChips = ({
  refinements,
  whitelist,
  activeRefine,
  refinementLabels,
}) => {
  const groupsToRender = (whitelist ?? [])
    .filter((id) => typeof id === 'string' && id.length > 0 && !FORCED_DISALLOW.has(id))
    .map((attributeId) => findRefinement(refinements, attributeId))
    .filter((r) => r && Array.isArray(r.values) && r.values.length > 0);

  // Flatten every group into a single horizontal chip row (scroll inside the tag bar when overflow).
  // Boolean groups collapse to a single on/off chip labelled with the attribute display name;
  // everything else renders one chip per value.
  const chips = groupsToRender.flatMap((group) => {
    if (isBooleanGroup(group)) {
      const trueValue =
        group.values.find((v) => BOOLEAN_TRUE_RE.test(String(v?.value))) ?? group.values[0];
      const apiValue = String(trueValue.value);
      const active = isRefineValueActive(activeRefine, group.attributeId, apiValue);
      const label = group.label || group.attributeId;
      const hitCount = typeof trueValue?.hitCount === 'number' ? trueValue.hitCount : null;
      return [{
        key: `bool:${group.attributeId}`,
        attributeId: group.attributeId,
        apiValue,
        label,
        active,
        hitCount,
      }];
    }

    return group.values.map((value) => {
      const apiValue = String(value.value);
      return {
        key: `${group.attributeId}:${apiValue}`,
        attributeId: group.attributeId,
        apiValue,
        label: value.label || apiValue,
        active: isRefineValueActive(activeRefine, group.attributeId, apiValue),
        hitCount: typeof value.hitCount === 'number' ? value.hitCount : null,
      };
    });
  });

  const chipKeys = new Set(chips.map((chip) => `${chip.attributeId}:${chip.apiValue}`));
  const booleanAttrsRendered = new Set(
    chips.filter((chip) => chip.key.startsWith('bool:')).map((chip) => chip.attributeId)
  );
  const ghostChips = [];

  for (const {attributeId, value} of parseRefineEntries(activeRefine)) {
    if (!isWhitelistedRefinementAttribute(whitelist, attributeId)) continue;
    if (FORCED_DISALLOW.has(attributeId)) continue;

    const apiValue = String(value);

    if (BOOLEAN_VALUE_RE.test(apiValue)) {
      if (booleanAttrsRendered.has(attributeId)) continue;
      booleanAttrsRendered.add(attributeId);
      ghostChips.push({
        key: `ghost:bool:${attributeId}`,
        attributeId,
        apiValue,
        label: resolveAttributeLabel(refinementLabels, attributeId),
        active: true,
        hitCount: null,
      });
      continue;
    }

    if (chipKeys.has(`${attributeId}:${apiValue}`)) continue;
    chipKeys.add(`${attributeId}:${apiValue}`);

    ghostChips.push({
      key: `ghost:${attributeId}:${apiValue}`,
      attributeId,
      apiValue,
      label: resolveValueLabel(refinementLabels, attributeId, apiValue),
      active: true,
      hitCount: null,
    });
  }

  return chips.concat(ghostChips);
};

export const RangeFilterTags = ({
  refinements,
  whitelist,
  activeRefine,
  refinementLabels,
  onToggle,
  ...rest
}) => {
  const intl = useIntl();

  if (!Array.isArray(whitelist) || whitelist.length === 0) return null;

  const chips = buildRangeFilterChips({
    refinements,
    whitelist,
    activeRefine,
    refinementLabels,
  });

  if (chips.length === 0) return null;

  const styles = useMultiStyleConfig('RangeFilterTags', {});
  const groupAriaLabel = intl.formatMessage({
    defaultMessage: 'Filter products',
    id: 'range_filter_tags.group.aria_label',
  });

  return (
    <Box
      aria-label={groupAriaLabel}
      data-testid="range-filter-tags"
      role="group"
      {...styles.wrapper}
      {...rest}
    >
      <Box {...styles.scrollTrack}>
        <Flex {...styles.scroll} role="list">
          {chips.map((chip) => (
            <Box key={chip.key} role="listitem" {...styles.chipItem}>
              <Button
                aria-pressed={chip.active}
                data-testid={`range-filter-tag-${chip.attributeId}-${chip.apiValue}`}
                type="button"
                variant="unstyled"
                {...styles.chip}
                onClick={() => onToggle?.(chip.attributeId, chip.apiValue)}
              >
                <Box as="span" {...styles.chipLabel}>
                  {chip.label}
                </Box>
                {chip.hitCount && chip.hitCount > 0 ? (
                  <Box as="span" {...styles.chipCount}>
                    <FormattedMessage
                      defaultMessage="({count})"
                      id="range_filter_tags.value.hit_count"
                      values={{count: chip.hitCount}}
                    />
                  </Box>
                ) : null}
              </Button>
            </Box>
          ))}
        </Flex>
      </Box>
    </Box>
  );
};

RangeFilterTags.displayName = 'RangeFilterTags';
RangeFilterTags.propTypes = {
  activeRefine: PropTypes.arrayOf(PropTypes.string),
  onToggle: PropTypes.func,
  refinementLabels: PropTypes.shape({
    attributes: PropTypes.instanceOf(Map),
    values: PropTypes.instanceOf(Map),
  }),
  refinements: PropTypes.array,
  whitelist: PropTypes.arrayOf(PropTypes.string),
};

export default RangeFilterTags;
